/**
 * Path normalization for event names.
 *
 * A raw pathname is unbounded: `/share/audio/abc-123` and `/share/audio/def-456`
 * are two values for one screen, so a breakdown fragments into thousands of rows
 * and the ids themselves land in PostHog. Both problems are the same fix, so the
 * scrubber lives here rather than once per surface.
 *
 * This is the fallback for runtimes that only ever see an interpolated path.
 * Where a real route template is available (Hono's `routePath`, openapi-fetch's
 * `schemaPath`) use that instead; it is exact where this is a heuristic.
 */

/**
 * Segments that carry an identity rather than naming a screen.
 *
 * Deliberately shape-based. A `user_`/`org_` prefix alone is not enough: it
 * swallowed ordinary route names, collapsing `user_settings`,
 * `team_dashboard` and `sub_processors` to `:id`. What separates an id from a
 * word is that the tail carries a digit, so that is what the rule requires.
 *
 * It is a heuristic and it will mis-read `org_settings2`. That direction is
 * the safe one: a scrubbed name loses a row, an unscrubbed id loses the whole
 * breakdown and ships user data.
 */
const ID_SEGMENT = new RegExp(
  [
    '^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$', // uuid, any version
    '^[0-9A-HJKMNP-TV-Z]{26}$', // ulid
    '^[0-9a-f]{16,}$', // long hex
    '^\\d+$', // numeric id
    '^(user|org|conv|msg|note|act|team|sub)_(?=[A-Za-z0-9]*\\d)[A-Za-z0-9]{3,}$', // prefixed id: tail carries a digit
  ].join('|'),
  'i',
)

/** Percent-encoded `@`, so an encoded address is caught by the same guard. */
const ENCODED_AT = /%40/i

export function normalizePath(pathname: string): string {
  // A query string or fragment is never part of a name, and dropping it beats
  // redacting it: the OTel path does the same, so no list of secret parameter
  // names has to be kept current. It also unblocks the segment match, which is
  // anchored and would otherwise skip `<uuid>?token=...` entirely.
  const pathOnly = pathname.split(/[?#]/, 1)[0]

  const normalized = pathOnly
    .split('/')
    .map((segment) => {
      if (!segment) {
        return segment
      }
      if (segment.includes('@') || ENCODED_AT.test(segment)) {
        return ':email'
      }
      return ID_SEGMENT.test(segment) ? ':id' : segment
    })
    .join('/')

  if (normalized === '/' || normalized === '') {
    return '/'
  }
  return normalized.replace(/\/$/, '')
}

/** A view name derived from a pathname: `/features/skills` becomes `features.skills`. */
export function viewNameFromPath(pathname: string): string {
  const normalized = normalizePath(pathname)
  if (normalized === '/') {
    return 'home'
  }
  return normalized.replace(/^\//, '').split('/').join('.')
}
